/**
 * PerformanceMonitor tracks and analyzes performance metrics
 */
export class PerformanceMonitor {
    constructor() {
        this.metrics = new Map();
        this.violations = [];
        this.thresholds = {
            pluginInit: 2,
            pluginExecute: 5,
            observerCallback: 2,
            totalBlocking: 50
        };
    }
    /**
     * Measure the performance of a function
     */
    async measure(name, fn) {
        const startTime = performance.now();
        try {
            const result = await Promise.resolve(fn());
            const duration = performance.now() - startTime;
            this.recordMetric(name, duration);
            this.checkThreshold(name, duration);
            return result;
        }
        catch (error) {
            const duration = performance.now() - startTime;
            this.recordMetric(name, duration);
            throw error;
        }
    }
    /**
     * Measure synchronous operations
     */
    measureSync(name, fn) {
        const startTime = performance.now();
        try {
            const result = fn();
            const duration = performance.now() - startTime;
            this.recordMetric(name, duration);
            this.checkThreshold(name, duration);
            return result;
        }
        catch (error) {
            const duration = performance.now() - startTime;
            this.recordMetric(name, duration);
            throw error;
        }
    }
    /**
     * Record a performance metric
     */
    recordMetric(name, duration) {
        if (!this.metrics.has(name)) {
            this.metrics.set(name, []);
        }
        this.metrics.get(name).push({
            name,
            duration,
            timestamp: performance.now()
        });
        // Update global metrics if available
        if (window.__OPTIMIZER_METRICS__) {
            window.__OPTIMIZER_METRICS__.totalBlockingTime += duration;
        }
    }
    /**
     * Check if a metric exceeds its threshold
     */
    checkThreshold(name, duration) {
        // Find matching threshold
        let threshold;
        // Check exact match
        if (this.thresholds[name] !== undefined) {
            threshold = this.thresholds[name];
        }
        else {
            // Check pattern matches
            for (const [pattern, value] of Object.entries(this.thresholds)) {
                if (name.includes(pattern)) {
                    threshold = value;
                    break;
                }
            }
        }
        if (threshold !== undefined && duration > threshold) {
            const violation = {
                metric: name,
                duration,
                threshold,
                timestamp: performance.now()
            };
            this.violations.push(violation);
            console.warn(`[PerformanceMonitor] Threshold exceeded: ${name} took ${duration.toFixed(2)}ms (threshold: ${threshold}ms)`);
        }
    }
    /**
     * Set custom thresholds
     */
    setThresholds(thresholds) {
        Object.entries(thresholds).forEach(([key, value]) => {
            if (value !== undefined) {
                this.thresholds[key] = value;
            }
        });
    }
    /**
     * Get a summary of all metrics
     */
    getSummary(metricName) {
        const summary = {};
        const metricsToSummarize = metricName
            ? [[metricName, this.metrics.get(metricName) || []]]
            : Array.from(this.metrics.entries());
        for (const [name, metrics] of metricsToSummarize) {
            if (metrics.length === 0)
                continue;
            const durations = metrics.map(m => m.duration);
            const total = durations.reduce((sum, d) => sum + d, 0);
            summary[name] = {
                count: metrics.length,
                total,
                average: total / metrics.length,
                min: Math.min(...durations),
                max: Math.max(...durations)
            };
        }
        return summary;
    }
    /**
     * Get a full performance report
     */
    getReport() {
        const metrics = {};
        for (const [name, metricList] of this.metrics) {
            metrics[name] = [...metricList];
        }
        return {
            metrics,
            summary: this.getSummary(),
            violations: [...this.violations]
        };
    }
    /**
     * Clear all recorded metrics
     */
    clear() {
        this.metrics.clear();
        this.violations = [];
    }
    /**
     * Get metrics for a specific operation
     */
    getMetrics(name) {
        return this.metrics.get(name) || [];
    }
    /**
     * Calculate percentile for a metric
     */
    getPercentile(name, percentile) {
        const metrics = this.getMetrics(name);
        if (metrics.length === 0)
            return null;
        const durations = metrics.map(m => m.duration).sort((a, b) => a - b);
        const index = Math.ceil((percentile / 100) * durations.length) - 1;
        return durations[Math.max(0, Math.min(index, durations.length - 1))] ?? null;
    }
    /**
     * Check if any performance budgets were exceeded
     */
    hasViolations() {
        return this.violations.length > 0;
    }
    /**
     * Create a performance mark
     */
    mark(name) {
        if ('performance' in window && 'mark' in performance) {
            performance.mark(name);
        }
    }
    /**
     * Measure between two marks
     */
    measureMarks(startMark, endMark, name) {
        if ('performance' in window && 'measure' in performance) {
            try {
                const measureName = name || `${startMark}-${endMark}`;
                performance.measure(measureName, startMark, endMark);
                const entries = performance.getEntriesByName(measureName);
                if (entries.length > 0) {
                    const lastEntry = entries[entries.length - 1];
                    if (lastEntry && 'duration' in lastEntry) {
                        const duration = lastEntry.duration;
                        this.recordMetric(measureName, duration);
                        return duration;
                    }
                }
            }
            catch (error) {
                console.error('[PerformanceMonitor] Error measuring marks:', error);
            }
        }
        return null;
    }
}
//# sourceMappingURL=performance-monitor.js.map